import React, { useEffect, useState } from "react";
import { DefaultButton, Modal, Label, Stack } from "@fluentui/react";
import { AssociatedUsersModalProps, AssociatedUser, Identity, RevokeAccessResult } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import { contentStyles, styleLabel, styleDefButton, SavingsPlanTable } from "@/screen/Identities/Styles/IdentitiesMainScreenStyles";
import CustomIcon from "@/components/icons/CustomIcon";
import ModalRevokeUserAccess from "@/screen/Identities/ModalRevokeUserAccess";

const MODAL_DISMISS_CLEAR_MS = 350;

const ModalAssociatedUsers: React.FC<AssociatedUsersModalProps> = ({isOpen, identity, identityFullName, onClose, onRevokeAccess}) =>
{
    const [userToRevoke, setUserToRevoke] = useState<AssociatedUser | null>(null);
    const [revokeError, setRevokeError] = useState<string | null>(null);
    const [revokeSubmitting, setRevokeSubmitting] = useState(false);
    const [displaySnapshot, setDisplaySnapshot] = useState<{identity: Identity; fullName: string} | null>(null);

    useEffect(() =>
    {
        if(isOpen && identity)
        {
            setDisplaySnapshot({identity, fullName: identityFullName});
        }
    }, [isOpen, identity, identityFullName]);

    useEffect(() =>
    {
        if(!isOpen)
        {
            const timer = window.setTimeout(() =>
            {
                setDisplaySnapshot(null);
                setUserToRevoke(null);
            }, MODAL_DISMISS_CLEAR_MS);
            return () =>
            {
                window.clearTimeout(timer);
            };
        }
        return undefined;
    }, [isOpen]);

    const shownIdentity = displaySnapshot?.identity ?? identity;
    const shownFullName = displaySnapshot?.fullName ?? identityFullName;

    const handleRevokeClick = (user: AssociatedUser) =>
    {
        setRevokeError(null);
        setUserToRevoke(user);
    };

    const handleConfirmRevoke = async () =>
    {
        if(!userToRevoke)
        {
            return;
        }
        setRevokeError(null);
        setRevokeSubmitting(true);
        let outcome: RevokeAccessResult;
        try
        {
            outcome = await onRevokeAccess(userToRevoke.id);
        }
        catch
        {
            outcome = {ok: false, message: "No se pudo completar la operación. Inténtalo de nuevo."};
        }
        finally
        {
            setRevokeSubmitting(false);
        }
        if(outcome.ok)
        {
            const remainingUsers = (shownIdentity?.associatedUsers ?? []).filter((user) => user.id !== userToRevoke.id);
            setUserToRevoke(null);
            setRevokeError(null);
            if(remainingUsers.length === 0)
            {
                onClose();
            }
        }
        else
        {
            setRevokeError(outcome.message);
        }
    };

    const handleCancelRevoke = () =>
    {
        setRevokeError(null);
        setUserToRevoke(null);
    };

    const formatDate = (date: Date): string =>
    {
        return new Date(date).toLocaleDateString("es-ES",
        {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    return(
        <Modal isOpen={isOpen}>
            <div className={contentStyles.header}>
                <Label styles={styleLabel}>
                    Usuarios asociados al interviniente <br />
                    <strong>{shownFullName}</strong>
                </Label>
            </div>
            <div className={contentStyles.body}>
                {shownIdentity && shownIdentity.associatedUsers.length > 0 ?
                (
                    <SavingsPlanTable>
                        <thead>
                            <tr>
                                <th>Correo electrónico</th>
                                <th>Fecha de Acceso</th>
                                <th>Revocar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shownIdentity.associatedUsers.map((user) =>
                            (
                                <tr key={user.id}>
                                    <td>{user.email}</td>
                                    <td>{formatDate(user.accessDate)}</td>
                                    <td>
                                        {user.canRevoke ?
                                        (
                                            <CustomIcon iconName="BlockContact" size="17" color="#CC214F" paddingTop="3%" onClick={() => handleRevokeClick(user)} />
                                        ) :
                                        (
                                            <span>-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </SavingsPlanTable>
                ) :
                (
                    <p>Este interviniente no tiene usuarios asociados.</p>
                )}
            </div>
            <Stack.Item style={{display: "flex", justifyContent: "center", padding: "10px"}}>
                <DefaultButton styles={styleDefButton} onClick={onClose}>
                    Cerrar
                </DefaultButton>
            </Stack.Item>
            <ModalRevokeUserAccess
                isOpen={userToRevoke !== null}
                userEmail={userToRevoke ? userToRevoke.email : ""}
                identityFullName={shownFullName}
                errorMessage={revokeError}
                isSubmitting={revokeSubmitting}
                onConfirm={handleConfirmRevoke}
                onCancel={handleCancelRevoke}
            />
        </Modal>
    );
};

export default ModalAssociatedUsers;